import React, { useState, useMemo, useEffect } from "react";
import { Sidebar } from "../components/ui/sidebar";
import GoalsHeader from "../components/ui/goals/goals-header";
import GoalProgressChart from "../components/ui/goals/goal-progress-chart";
import GoalsStats from "../components/ui/goals/goals-stats";
import ActiveGoals from "../components/ui/goals/active-goals";
import { ProgressStats } from "../components/ui/goals/progress-stats";
import { WeeklyGoalsSection } from "../components/ui/goals/weekly-goals-section";
import { CareerSpotlight } from "../components/ui/goals/career-spotlight";
import AchievementsHeader from "../components/ui/achievements/achievements-header";
import XpProgress from "../components/ui/achievements/xp-progress";
import AchievementStats from "../components/ui/achievements/achievement-stats";
import AchievementsGrid from "../components/ui/achievements/achievements-grid";
import { UniversalSearch } from "../components/ui/universal-search";
import ProtectedRoute from "../components/ProtectedRoute";
import { StorageService } from "@/hooks/services/storageService";
import { AuthStore } from "@/hooks/stores/AuthStore";
import { StudentStore } from "@/hooks/stores/StudentStore";
import { Goal, Trophy, Medal, Crown, Star, Flame, TrendingUp } from "lucide-react";
import api from "@/hooks/api/apiClient";
import config from "../../config";

// ─── Achievement catalog (merged from Achievements page) ──────────────────────
const CATALOG = [
  { id: "first_steps",      name: "First Steps",       description: "Joined EarlyScape",                            icon: "Star",          rarity: "common", points: 10,  badgeColor: "#6366f1", check: () => ({ unlocked: true, progress: 100 }) },
  { id: "onboarding_done",  name: "Ready to Go",        description: "Completed onboarding wizard",                  icon: "CheckCircle",   rarity: "common", points: 20,  badgeColor: "#10b981", check: (s) => ({ unlocked: s.metadata?.onboardingCompleted === true, progress: s.metadata?.onboardingCompleted ? 100 : 0 }) },
  { id: "self_explorer",    name: "Self Explorer",      description: "Completed psychometric assessment",             icon: "Brain",         rarity: "common", points: 75,  badgeColor: "#6366f1", check: (s) => { const done = s.traitProfile != null && Object.keys(s.traitProfile).length > 0; return { unlocked: done, progress: done ? 100 : 0 }; } },
  { id: "path_finder",      name: "Path Finder",        description: "Selected your first career path",               icon: "MapPin",        rarity: "common", points: 50,  badgeColor: "#0ea5e9", check: (s) => { const done = (s.selectedBroadPaths?.length ?? 0) > 0; return { unlocked: done, progress: done ? 100 : 0 }; } },
  { id: "multi_path",       name: "Multi-Tracker",      description: "Selected 3 or more career paths",               icon: "Rocket",        rarity: "rare",   points: 100, badgeColor: "#8b5cf6", check: (s) => { const count = s.selectedBroadPaths?.length ?? 0; return { unlocked: count >= 3, progress: Math.min(100, Math.round((count / 3) * 100)) }; } },
  { id: "career_explorer",  name: "Career Explorer",    description: "Explored 3+ career interests",                  icon: "Globe",         rarity: "common", points: 50,  badgeColor: "#0ea5e9", check: (s) => { const count = s.careerInterests?.length ?? 0; return { unlocked: count >= 3, progress: Math.min(100, Math.round((count / 3) * 100)) }; } },
  { id: "career_committed", name: "Career Committed",   description: "Explored 5+ career interests",                  icon: "Target",        rarity: "rare",   points: 100, badgeColor: "#f59e0b", check: (s) => { const count = s.careerInterests?.length ?? 0; return { unlocked: count >= 5, progress: Math.min(100, Math.round((count / 5) * 100)) }; } },
  { id: "scholar",          name: "Scholar",            description: "Uploaded academic results",                     icon: "Calculator",    rarity: "common", points: 60,  badgeColor: "#6366f1", check: (s) => { const done = (s.academicResults?.length ?? 0) > 0; return { unlocked: done, progress: done ? 100 : 0 }; } },
  { id: "profile_complete", name: "Profile Complete",   description: "Profile completeness reached 80%",              icon: "Award",         rarity: "rare",   points: 80,  badgeColor: "#0ea5e9", check: (s) => { const pct = s.metadata?.profileCompleteness ?? 0; return { unlocked: pct >= 80, progress: Math.min(100, pct) }; } },
  { id: "rising_talent",    name: "Rising Talent",      description: "Reached 70+ in any trait score",                icon: "BarChart2",     rarity: "rare",   points: 100, badgeColor: "#8b5cf6", check: (s) => { const top = s.traitProfile ? Math.max(0, ...Object.values(s.traitProfile)) : 0; return { unlocked: top >= 70, progress: Math.min(100, Math.round((top / 70) * 100)) }; } },
  { id: "trait_master",     name: "Trait Master",       description: "Reached 80+ in any trait score",                icon: "Zap",           rarity: "epic",   points: 200, badgeColor: "#ef4444", check: (s) => { const top = s.traitProfile ? Math.max(0, ...Object.values(s.traitProfile)) : 0; return { unlocked: top >= 80, progress: Math.min(100, Math.round((top / 80) * 100)) }; } },
  { id: "on_fire",          name: "On Fire",            description: "Assessment done, path chosen, journey started", icon: "Flame",         rarity: "epic",   points: 150, badgeColor: "#ef4444", check: (s) => { const hasTraits = s.traitProfile != null && Object.keys(s.traitProfile).length > 0; const hasPaths = (s.selectedBroadPaths?.length ?? 0) > 0; const met = [hasTraits, hasPaths].filter(Boolean).length; return { unlocked: met === 2, progress: Math.round((met / 2) * 100) }; } },
];
function computeAchievements(student) {
  return CATALOG.map(({ check, ...def }) => {
    const { unlocked, progress } = student ? check(student) : { unlocked: false, progress: 0 };
    return { ...def, isUnlocked: unlocked, progress };
  });
}
function computeXP(student) {
  const traitXP    = (student?.traitEvents  ?? []).reduce((sum, e) => sum + Math.abs(e.delta ?? 0), 0);
  const activityXP = (student?.activityLog  ?? []).length * 5;
  const goalXP     = (student?.goals        ?? []).filter(g => g.status === "completed").length * 20;
  return Math.round(traitXP + activityXP + goalXP) || student?.totalPoints || 0;
}
function computeLevel(xp) { return Math.max(1, Math.floor(xp / 100) + 1); }

const RANK_STYLES = [
  { bg: "bg-amber-50 border-amber-200",   text: "text-amber-600",  icon: <Crown className="w-5 h-5 text-amber-500" /> },
  { bg: "bg-slate-100 border-slate-200",  text: "text-slate-500",  icon: <Medal className="w-5 h-5 text-slate-400" /> },
  { bg: "bg-orange-50 border-orange-200", text: "text-orange-600", icon: <Medal className="w-5 h-5 text-orange-400" /> },
];

function LeaderboardTab({ currentUser }) {
  const { ranked, currentRank } = useMemo(() => {
    const students = StorageService.read("students") ?? [];
    const ranked = students
      .map((s) => ({
        id: s._id ?? s.id ?? s.userId,
        name: `${s.firstName ?? s.name ?? "Student"} ${s.lastName ?? ""}`.trim(),
        avatar: (s.firstName ?? s.name ?? "S")[0].toUpperCase(),
        classLevel: s.classLevel ?? s.preferences?.classLevel ?? "—",
        xp: computeXP(s),
        level: computeLevel(computeXP(s)),
        streak: s.streakDays ?? 0,
        isMe: s.userId === currentUser?._id || s.email === currentUser?.email,
      }))
      .sort((a, b) => b.xp - a.xp)
      .map((s, i) => ({ ...s, rank: i + 1 }));
    return { ranked, currentRank: ranked.find(s => s.isMe) };
  }, [currentUser]);
  const top3 = ranked.slice(0, 3);
  const rest = ranked.slice(3);
  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <p className="text-slate-500 text-sm">School-wide ranking by career XP</p>
      {currentRank && (
        <div className="bg-indigo-600 text-white rounded-2xl p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-black text-lg">{currentRank.avatar}</div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm">You — {currentRank.name}</p>
            <p className="text-indigo-200 text-xs">{currentRank.xp} XP · Level {currentRank.level}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-3xl font-black">#{currentRank.rank}</p>
            <p className="text-indigo-200 text-xs">of {ranked.length}</p>
          </div>
        </div>
      )}
      {top3.length > 0 && (
        <div className="flex items-end justify-center gap-4">
          {top3[1] && (<div className="flex flex-col items-center gap-2 flex-1"><div className={`w-14 h-14 rounded-full border-2 flex items-center justify-center font-black text-xl ${top3[1].isMe ? "bg-indigo-100 border-indigo-400 text-indigo-700" : "bg-slate-200 border-slate-300 text-slate-600"}`}>{top3[1].avatar}</div><div className="text-center"><p className="text-xs font-bold text-slate-700 truncate max-w-20">{top3[1].name.split(" ")[0]}</p><p className="text-xs text-slate-400">{top3[1].xp} XP</p></div><div className="w-full bg-slate-300 rounded-t-xl h-16 flex items-center justify-center"><Medal className="w-6 h-6 text-slate-500" /></div></div>)}
          {top3[0] && (<div className="flex flex-col items-center gap-2 flex-1"><Crown className="w-6 h-6 text-amber-500" /><div className={`w-16 h-16 rounded-full border-2 flex items-center justify-center font-black text-2xl ${top3[0].isMe ? "bg-indigo-100 border-indigo-400 text-indigo-700" : "bg-amber-100 border-amber-300 text-amber-700"}`}>{top3[0].avatar}</div><div className="text-center"><p className="text-sm font-bold text-slate-800 truncate">{top3[0].name.split(" ")[0]}</p><p className="text-xs text-slate-500">{top3[0].xp} XP</p></div><div className="w-full bg-amber-400 rounded-t-xl h-24 flex items-center justify-center"><Star className="w-7 h-7 text-white" /></div></div>)}
          {top3[2] && (<div className="flex flex-col items-center gap-2 flex-1"><div className={`w-14 h-14 rounded-full border-2 flex items-center justify-center font-black text-xl ${top3[2].isMe ? "bg-indigo-100 border-indigo-400 text-indigo-700" : "bg-orange-100 border-orange-300 text-orange-700"}`}>{top3[2].avatar}</div><div className="text-center"><p className="text-xs font-bold text-slate-700 truncate max-w-20">{top3[2].name.split(" ")[0]}</p><p className="text-xs text-slate-400">{top3[2].xp} XP</p></div><div className="w-full bg-orange-300 rounded-t-xl h-10 flex items-center justify-center"><Medal className="w-5 h-5 text-white" /></div></div>)}
        </div>
      )}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {ranked.map((s) => {
          const rankStyle = RANK_STYLES[s.rank - 1];
          return (
            <div key={s.id} className={`flex items-center gap-4 px-5 py-3.5 border-b border-slate-50 last:border-0 transition-colors ${s.isMe ? "bg-indigo-50" : "hover:bg-slate-50"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black border shrink-0 ${rankStyle ? `${rankStyle.bg} ${rankStyle.text}` : "bg-slate-50 border-slate-200 text-slate-400"}`}>{rankStyle ? rankStyle.icon : `#${s.rank}`}</div>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${s.isMe ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-600"}`}>{s.avatar}</div>
              <div className="flex-1 min-w-0"><p className={`text-sm font-bold truncate ${s.isMe ? "text-indigo-700" : "text-slate-800"}`}>{s.name}{s.isMe && <span className="text-xs font-medium text-indigo-400 ml-1">(you)</span>}</p><p className="text-xs text-slate-400">{s.classLevel} · Lv {s.level}</p></div>
              {s.streak > 0 && <div className="flex items-center gap-1 text-xs font-semibold text-orange-500"><Flame className="w-3.5 h-3.5" /> {s.streak}d</div>}
              <div className="text-right shrink-0"><p className="text-sm font-black text-slate-800">{s.xp.toLocaleString()}</p><p className="text-[10px] text-slate-400 uppercase tracking-wide">XP</p></div>
            </div>
          );
        })}
        {ranked.length === 0 && (
          <div className="text-center py-16 text-slate-400"><TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-40" /><p className="font-medium">No students on the board yet.</p><p className="text-sm mt-1">Complete activities to earn XP and appear here.</p></div>
        )}
      </div>
    </div>
  );
}

// ─── Load goals from StorageService ──────────────────────────────────────────
function loadStorageGoals() {
  const user = AuthStore.getState().currentUser;
  const userId = user?._id;
  const all = StorageService.read("goals");
  return Array.isArray(all) && userId
    ? all.filter((g) => g.userId === userId)
    : [];
}

// ─── Map stored goal → shape expected by GoalCard / ActiveGoals ──────────────
const CATEGORY_ICON = {
  academic: "Calculator", career: "Gamepad2",
  personal: "BookOpen",  skill: "Rocket",
  extracurricular: "ClipboardList",
};
const CATEGORY_COLORS = {
  academic:        { bg: "bg-blue-100",   text: "text-blue-500"   },
  career:          { bg: "bg-orange-100", text: "text-orange-500" },
  personal:        { bg: "bg-emerald-100",text: "text-emerald-500"},
  skill:           { bg: "bg-purple-100", text: "text-purple-500" },
  extracurricular: { bg: "bg-amber-100",  text: "text-amber-500"  },
};

function mapGoal(g) {
  const colors = CATEGORY_COLORS[g.category] ?? { bg: "bg-gray-100", text: "text-gray-500" };
  return {
    id: g.id,
    title: g.title,
    description: g.description,
    progress: g.progress ?? 0,
    dueDate: g.deadline
      ? new Date(g.deadline).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : "",
    icon: g.icon ? undefined : (CATEGORY_ICON[g.category] ?? "BookOpen"),
    iconEmoji: g.icon,
    iconBgColor: colors.bg,
    iconColor: colors.text,
    isCompleted: (g.progress ?? 0) >= 100 || g.status === "completed",
    completedDate: g.status === "completed" ? "Completed" : undefined,
    tier: g.tier,
    goalType: g.goalType,
  };
}

export default function GoalsPage() {
  const [pageTab, setPageTab] = useState("goals");
  const [goals, setGoals] = useState(() => loadStorageGoals().map(mapGoal));

  // Achievements state
  const currentUser   = AuthStore((s) => s.currentUser);
  const studentData   = StudentStore((s) => s.student);
  const achievements  = useMemo(() => computeAchievements(studentData), [studentData]);
  const totalPoints   = achievements.filter(a => a.isUnlocked).reduce((sum, a) => sum + (a.points ?? 0), 0);
  const currentStreak = studentData?.engagementData?.loginFrequency ?? 0;
  const currentXp     = totalPoints % 1000;

  // Load from backend on mount (non-prototype)
  useEffect(() => {
    if (config.appMode === "prototype") return;
    api.get("/goals")
      .then((res) => {
        const items = res.data?.data?.goals ?? [];
        if (items.length > 0) {
          const normalized = items.map((g) => ({ ...g, id: g._id ?? g.id }));
          setGoals(normalized.map(mapGoal));
        }
      })
      .catch(() => {});
  }, []);

  // ✅ Add new goal
  const handleAddGoal = async (newGoal) => {
    const user = AuthStore.getState().currentUser;
    const userId = user?._id ?? "demo-user";
    const id = `goal_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const categoryMap = {
      Gamepad2: "career", Calculator: "academic",
      BookOpen: "personal", Beaker: "skill",
      Music: "personal", Rocket: "skill", ClipboardList: "extracurricular",
    };

    const stored = {
      id, _id: id, userId,
      title: newGoal.title,
      description: newGoal.description ?? "",
      category: categoryMap[newGoal.icon] ?? "personal",
      targetValue: 1, currentValue: newGoal.progress === 100 ? 1 : 0,
      progress: newGoal.progress ?? 0,
      status: newGoal.progress >= 100 ? "completed" : newGoal.progress > 0 ? "in-progress" : "not-started",
      priority: "medium", difficulty: "medium", xpReward: 20,
      deadline: newGoal.dueDate ? new Date(newGoal.dueDate) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      createdAt: new Date(), updatedAt: new Date(),
      autoGenerated: false,
    };

    if (config.appMode === "prototype") {
      const all = StorageService.read("goals");
      StorageService.write("goals", [...(Array.isArray(all) ? all : []), stored]);
    } else {
      try {
        const res = await api.post("/goals", {
          title: stored.title,
          description: stored.description,
          category: stored.category,
          priority: stored.priority,
          difficulty: stored.difficulty,
          targetDate: stored.deadline,
        });
        const created = res.data?.data?.goal;
        if (created) {
          stored.id = created._id;
          stored._id = created._id;
        }
      } catch (_) {}
    }

    setGoals((prev) => [...prev, mapGoal(stored)]);
  };

  // ✅ Delete goal
  const handleDeleteGoal = (id) => {
    setGoals((prev) => prev.filter((goal) => goal.id !== id));
    if (config.appMode === "prototype") {
      const all = StorageService.read("goals");
      StorageService.write("goals", Array.isArray(all) ? all.filter((g) => g.id !== id) : []);
    } else {
      api.delete(`/goals/${id}`).catch(() => {});
    }
  };

  // ✅ Edit goal
  const handleEditGoal = (id, updatedGoal) => {
    setGoals((prev) =>
      prev.map((goal) => (goal.id === id ? { ...goal, ...updatedGoal } : goal))
    );
    if (config.appMode === "prototype") {
      const all = StorageService.read("goals");
      if (Array.isArray(all)) {
        const idx = all.findIndex((g) => g.id === id);
        if (idx >= 0) {
          all[idx] = { ...all[idx], ...updatedGoal, updatedAt: new Date() };
          StorageService.write("goals", all);
        }
      }
    } else {
      api.patch(`/goals/${id}`, updatedGoal).catch(() => {});
    }
  };

  const handleSearch = (query) => {
    console.log("Universal search for:", query);
  };

  // ✅ Progress stats
  const completedCount = goals.filter((g) => g.isCompleted).length;
  const inProgressCount = goals.filter((g) => !g.isCompleted).length;
  const overallProgress =
    goals.length > 0 ? Math.round((completedCount / goals.length) * 100) : 0;

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-200">
      {/* Sidebar (self-contained and fixed on desktop) */}
      <Sidebar />

      

      {/* Universal Search Component */}
      <UniversalSearch onSearch={handleSearch} />

      {/* Main content area */}
      <div className="flex-1 flex flex-col w-full transition-all duration-300 ease-in-out lg:ml-64">
        {/* ✅ Mobile header (hamburger menu display only) */}
        {/* Mobile header */}
        <div className="md:hidden flex items-center gap-3 p-4 border-b border-slate-200 bg-white sticky top-0 z-30">
          <button
            className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900">Goals & Achievements</h1>
            <p className="text-sm text-slate-600">Track your learning journey</p>
          </div>
        </div>

        <main className="flex-1 p-4 sm:p-6 md:p-8 overflow-y-auto">
          <div className="max-w-6xl mx-auto">

            {/* Page tab switcher */}
            <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 w-fit shadow-sm mb-6">
              {[
                { id: "goals",        label: "Goals",        Icon: Goal    },
                { id: "achievements", label: "Achievements", Icon: Trophy  },
                { id: "leaderboard",  label: "Leaderboard",  Icon: Medal   },
              ].map(({ id, label, Icon }) => (
                <button
                  key={id}
                  onClick={() => setPageTab(id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    pageTab === id
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>

            {/* ── Goals tab ── */}
            {pageTab === "goals" && (
              <>
                <div className="hidden md:block mb-6">
                  <GoalsHeader onAddGoal={handleAddGoal} />
                </div>
                <div className="md:hidden mb-6">
                  <button
                    onClick={() => handleAddGoal({ title: "New Goal", description: "", progress: 0, dueDate: "", icon: "BookOpen", iconBgColor: "bg-blue-100", iconColor: "text-blue-500", isCompleted: false })}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-medium transition-colors min-h-[44px] flex items-center justify-center"
                  >
                    Add New Goal
                  </button>
                </div>
                <div className="mb-6 sm:mb-8 overflow-x-auto">
                  <ProgressStats />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 lg:gap-10 mb-6">
                  <div className="lg:col-span-2 space-y-4 sm:space-y-6 md:space-y-8">
                    <div className="rounded-xl sm:rounded-2xl bg-slate-100 shadow-sm border border-slate-200 p-4 sm:p-6 md:p-8">
                      <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                        <GoalProgressChart progress={overallProgress} />
                        <GoalsStats completed={completedCount} inProgress={inProgressCount} total={goals.length} />
                      </div>
                    </div>
                    <WeeklyGoalsSection />
                  </div>
                  <aside className="lg:col-span-1">
                    <CareerSpotlight />
                  </aside>
                </div>
                <ActiveGoals goals={goals} onDeleteGoal={handleDeleteGoal} onEditGoal={handleEditGoal} />
              </>
            )}

            {/* ── Achievements tab ── */}
            {pageTab === "achievements" && (
              <div className="space-y-4 sm:space-y-6">
                <div className="hidden md:block">
                  <AchievementsHeader />
                </div>
                <XpProgress current={currentXp} max={1000} />
                <div className="overflow-x-auto">
                  <AchievementStats
                    totalPoints={totalPoints}
                    classRank={currentUser?.rank ?? 0}
                    totalStudents={28}
                    currentStreak={currentStreak}
                  />
                </div>
                <AchievementsGrid achievements={achievements} />
              </div>
            )}

            {/* ── Leaderboard tab ── */}
            {pageTab === "leaderboard" && <LeaderboardTab currentUser={currentUser} />}

          </div>
        </main>
      </div>
    </div>
    </ProtectedRoute>
  );
}